import { Vector as VectorLayer } from 'ol/layer'
import useMap from 'hooks/use-map'
import { getCropStyle, defaultStyle } from 'common/ol/styles'
import { get, isEmpty } from 'es-toolkit/compat'

import useSource from 'hooks/use-source-boundary'
import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { transformExtent } from 'ol/proj'
import { containsExtent } from 'ol/extent'
import { fetchFeaturesByBbox } from 'api/field'

export const FIELD_OVERVIEW_MIN_ZOOM = 12.9

export default function MapFieldsOverview(props) {
  const { features } = props
  const dispatch = useDispatch()
  const map = useMap()
  const source = useSource()
  const lastFetchedExtent = useRef(null)

  const fieldsHarvested = useSelector((state) =>
    get(state, `field.fieldsHarvested`),
  )
  const year = useSelector((state) => get(state, 'field.year'))
  const allFields = useSelector((state) => get(state, `field.collection`))
  const allFarms = useSelector((state) => get(state, `farm.collection`, []))

  const cffEnabled = useSelector((state) =>
    get(state, 'account.singleton.prefs.cff.enabled'),
  )

  const crop = useSelector((state) => get(state, `field.crop`))
  const crops = useSelector((state) => state.crop['cropConstantsCollection'])
  const client = useSelector((state) => get(state, `client.singleton`))
  const farms = useSelector((state) => get(state, `farm.selectedFarms`))
  const featuresOverview = useSelector((state) =>
    get(state, `field.featuresOverview`),
  )
  const featuresOverviewRef = useRef(featuresOverview)

  const sideHeaderVisibility = useSelector((state) =>
    get(state, `app.sideHeaderVisible`),
  )
  const [featuresMap, setFeaturesMap] = useState({})
  const [hiddenFields, setHiddenFields] = useState([])
  const [harvestedFields, setHarvestFields] = useState([])
  const [filteredFields, setFilteredFields] = useState([])

  const [layer, setLayer] = useState()
  const [menuElement, setMenuElement] = useState(
    document.getElementById('menu-'),
  )

  useEffect(() => {
    featuresOverviewRef.current = featuresOverview
    if (layer) {
      layer.changed()
    }
  }, [featuresOverview, layer])

  useEffect(() => {
    const _featuresMap = {}
    const featureArray = features.getArray()
    for (let i = 0; i < featureArray.length; i++) {
      const id = featureArray[i].getId()
      _featuresMap[id] = featureArray[i]
    }
    setFeaturesMap(_featuresMap)
  }, [layer, features])

  useEffect(() => {
    map.updateSize()
  }, [sideHeaderVisibility, menuElement])

  useEffect(() => {
    if (map) {
      const _layer = new VectorLayer({
        className: 'bfield-overview-layer',
        updateWhileAnimating: false,
        renderBuffer: 0,
        renderOrder: null,
        zIndex: 1,
        style: cropStyleFunction,
        minZoom: FIELD_OVERVIEW_MIN_ZOOM,
      })
      setLayer(_layer)
    }
  }, [map])

  useEffect(() => {
    if (layer) {
      map.addLayer(layer)
    }

    return () => {
      map.removeLayer(layer)
    }
  }, [map, layer])

  useEffect(() => {
    if (layer && source && features) {
      source.clear()
      source.addFeatures(features)
      layer.setSource(source)
    }
  }, [layer, source, features])

  useEffect(() => {
    if (!map) return

    lastFetchedExtent.current = null

    const fetchFeatures = () => {
      const zoom = map.getView().getZoom()
      if (zoom > FIELD_OVERVIEW_MIN_ZOOM) {
        const extent = map.getView().calculateExtent(map.getSize())
        const extent4326 = transformExtent(extent, 'EPSG:3857', 'EPSG:4326')

        if (
          lastFetchedExtent.current &&
          containsExtent(lastFetchedExtent.current, extent4326)
        ) {
          return
        }

        const width = extent4326[2] - extent4326[0]
        const height = extent4326[3] - extent4326[1]
        const bufferX = width * 0.2
        const bufferY = height * 0.2

        const bufferedExtent = [
          extent4326[0] - bufferX,
          extent4326[1] - bufferY,
          extent4326[2] + bufferX,
          extent4326[3] + bufferY,
        ]
        lastFetchedExtent.current = bufferedExtent
        dispatch(fetchFeaturesByBbox(bufferedExtent, year))
      }
    }

    const onMoveEnd = () => {
      fetchFeatures()
    }

    map.on('moveend', onMoveEnd)
    fetchFeatures()

    return () => {
      map.un('moveend', onMoveEnd)
    }
  }, [map, year, dispatch])

  useEffect(() => {
    if (fieldsHarvested) {
      let filteredFieldIds = []
      for (let i = 0; i < fieldsHarvested.length; i++) {
        filteredFieldIds.push(fieldsHarvested[i].id)
      }
      setHarvestFields(filteredFieldIds)
    }
  }, [fieldsHarvested])

  useEffect(() => {
    let visible = []
    const currentFeatures = source.getFeaturesCollection().getArray()
    if (hiddenFields.length > 0) {
      for (let i = currentFeatures.length - 1; i >= 0; i--) {
        const feature = currentFeatures[i]
        const id = feature.getId()
        if (hiddenFields.includes(id)) {
          try {
            source.removeFeature(feature)
          } catch (e) {
            console.log('failed to remove: ', id)
          }
        } else {
          visible.push(id)
        }
      }
    } else {
      for (let i = currentFeatures.length - 1; i >= 0; i--) {
        const feature = currentFeatures[i]
        const id = feature.getId()
        visible.push(id)
      }
    }

    if (crop !== 'all') {
      if (harvestedFields.length) {
        for (let i = 0; i < harvestedFields.length; i++) {
          const id = harvestedFields[i]
          const feature = featuresMap[id]
          if (!visible.includes(id) && feature) {
            source.addFeature(feature)
          }
        }
      }
    } else {
      for (let i = 0; i < filteredFields.length; i++) {
        const id = filteredFields[i]
        const feature = featuresMap[id]
        if (!visible.includes(id) && feature) {
          source.addFeature(feature)
        }
      }
    }
  }, [hiddenFields, harvestedFields, featuresMap, filteredFields, crop])

  useEffect(() => {
    let _filteredFields = []
    let farmIds = []
    if (cffEnabled) {
      if (farms?.length) {
        farmIds = farms.map((f) => f.id)
      }
      if (farmIds.length) {
        _filteredFields = allFields.filter((f) => farmIds.includes(f.farmId))
      }
    } else {
      _filteredFields = allFields
    }
    setFilteredFields(_filteredFields.map((f) => f.id))
  }, [client, farms, allFields, allFarms])

  useEffect(() => {
    if (!isEmpty(featuresMap)) {
      const featuresArray = features.getArray()
      if (crop == 'all') {
        let removedFeatures = []
        for (let i = 0; i < featuresArray.length; i++) {
          const id = featuresArray[i].getId()
          const found = filteredFields.includes(id)
          if (!found) {
            removedFeatures.push(id)
          }
        }
        setHiddenFields(removedFeatures)
      } else {
        let removedFeatures = []
        for (let i = 0; i < featuresArray.length; i++) {
          const id = featuresArray[i].getId()
          const found = filteredFields.includes(id)
          if (!harvestedFields.includes(id) || !found) {
            removedFeatures.push(id)
          }
        }
        setHiddenFields(removedFeatures)
      }
    }
  }, [harvestedFields, features, source, crop, featuresMap, filteredFields])

  function cropStyleFunction(feature, resolution) {
    if (feature) {
      const id = feature.getId()
      const cropId = featuresOverviewRef.current.find(
        (f) =>
          f.id == id ||
          f.featureId == id ||
          (f.featureId && f.featureId.toString() == id) ||
          (f.id && f.id.toString() == id),
      )?.cropId
      if (cropId) {
        const cropColor = crops.find((c) => c.id === cropId)?.color
        if (cropColor) {
          return getCropStyle(cropId, cropColor)['normal']
        }
      }
    }
    return defaultStyle
  }

  return null
}
